import { AxiosResponse } from "axios";
import {
    FilterBaseRequest,
    ImagesResponse,
    FilesResponse,
    TextFilterResponse,
    TextResponse,
    UserDataContext,
    TextUpdateRequest,
    ImageUsageResponse,
    ChangeResponse,
    Gallery,
    GallerySaveRequest,
    GalleriesFilterResponse,
    GalleryFilterRequest,
    GetPagesForWebsiteResponse,
    GalleryUsageResponse,
    PageImagePositionResponse,
    UpdatePageImagePositionRequest,
    UserFilterRequest,
    UserFilterResponse,
    GetWebsitesResponse,
    AdminUserCreateRequest,
    UserResponse,
    AdminUserUpdateRequest,
    AdminSetPasswordRequest,
    ListItemResponse,
    ListItemCreateRequest,
    FrontendEntityChangeListResponse,
    PagesFilterResponse,
    TextFilterRequest,
    Section,
    SectionFilterResponse,
    UpdateSectionRequest, Post, PostFilterRequest, PostFilterResponse, PostGetRequest, SectionFilterRequest,
    FileUsageResponse, FileItem,
    AdminUserNamesResponse
} from "../Models/Models";
import axiosInstance from './BackendService';

class HttpRepository {
    private static axiosInstance = axiosInstance;

    // region User

    static filterUsers(request: UserFilterRequest): Promise<AxiosResponse<UserFilterResponse>> {
        const params = {limit: request.limit, offset: request.offset, orderDirection: request.orderDirection};
        return HttpRepository.axiosInstance.get<UserFilterResponse>("/AdminUser", {params});
    }

    static login(email: string, password: string): Promise<AxiosResponse> {
        const params = new URLSearchParams();
        params.append('email', email);
        params.append('password', password);
        return HttpRepository.axiosInstance.post("/Authorization/login", params);
    }

    static logout(): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.delete("/Authorization/logout");
    }

    static user(): Promise<AxiosResponse<UserDataContext>>{
        return HttpRepository.axiosInstance.get<UserDataContext>("/Authorization/me");
    }

    static createUser(request: AdminUserCreateRequest): Promise<AxiosResponse<UserResponse>>{
        return HttpRepository.axiosInstance.post<UserResponse>("/AdminUser", request);
    }

    static udpateUser(request: AdminUserUpdateRequest): Promise<AxiosResponse<UserResponse>>{
        return HttpRepository.axiosInstance.put<UserResponse>("/AdminUser", request);
    }

    static setPassword(request: AdminSetPasswordRequest): Promise<AxiosResponse<UserResponse>>{
        return HttpRepository.axiosInstance.put<UserResponse>("/AdminUser/setPassword", request);
    }

    static getUserById(id: number): Promise<AxiosResponse<UserResponse>> {
        return HttpRepository.axiosInstance.get<UserResponse>(`/AdminUser/${id}`);
    }

    static getAdminUserNames(): Promise<AxiosResponse<AdminUserNamesResponse>> {
        return HttpRepository.axiosInstance.get<AdminUserNamesResponse>("/AdminUser/names");
    }

    // endregion

    // region Post

    static filterPosts(request: PostFilterRequest): Promise<AxiosResponse<PostFilterResponse>> {
        const params = {websiteId: request.websiteId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection, state: request.state};
        return HttpRepository.axiosInstance.get<PostFilterResponse>("/Post", {params});
    }

    static getPost(request: PostGetRequest): Promise<AxiosResponse<Post>> {
        return HttpRepository.axiosInstance.get<Post>(`/Post/${request.id}?websiteId=${request.websiteId}`);
    }

    static createPost(post: Post): Promise<AxiosResponse<ChangeResponse>> {
        return HttpRepository.axiosInstance.post<ChangeResponse>("/Post", post);
    }

    static updatePost(post: Post): Promise<AxiosResponse<ChangeResponse>> {
        return HttpRepository.axiosInstance.put<ChangeResponse>("/Post", post);
    }

    static deletePost(id: number): Promise<AxiosResponse<ChangeResponse>> {
        return HttpRepository.axiosInstance.delete<ChangeResponse>(`/Post/${id}`);
    }

    // endregion

    // region Text

    static filterTexts(request: TextFilterRequest): Promise<AxiosResponse<TextFilterResponse>> {
        const params = {websiteId: request.websiteId, pageId: request.pageId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection};
        return HttpRepository.axiosInstance.get<TextFilterResponse>("/Text", {params});
    }

    static getText(id: number): Promise<AxiosResponse<TextResponse>> {
        return HttpRepository.axiosInstance.get<TextResponse>(`/Text/${id}`);
    }

    static updateText(request: TextUpdateRequest): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.put("/Text", request);
    }

    // endregion

    // region Image

    static filterImages(request: FilterBaseRequest): Promise<AxiosResponse<ImagesResponse>> {
        const params = {websiteId: request.websiteId, limit: request.limit, offset: request.offset};
        return HttpRepository.axiosInstance.get<ImagesResponse>("/Image", { params });
    }

    static checkImageUsage(id: number): Promise<AxiosResponse<ImageUsageResponse>> {
        return HttpRepository.axiosInstance.get<ImageUsageResponse>(`/Image/${id}`);
    }

    static async uploadImages(formData: FormData) {
        return HttpRepository.axiosInstance.post('/Image/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
    }

    static updateImage(imageId: number, newAltText: string): Promise<AxiosResponse<ChangeResponse>> {
        return HttpRepository.axiosInstance.put<ChangeResponse>("/Image", { imageId, newAltText });
    }

    static getPageImage(pageImagePositionId: number): Promise<AxiosResponse<PageImagePositionResponse>> {
        return HttpRepository.axiosInstance.get<PageImagePositionResponse>(`/Image/pageImagePosition/${pageImagePositionId}`);
    }

    static updatePageImage(request: UpdatePageImagePositionRequest): Promise<AxiosResponse> {
        console.warn(request);
        return HttpRepository.axiosInstance.put("/Image/page", request);
    }

    static deleteImage(id: number): Promise<AxiosResponse<ChangeResponse>> {
        return HttpRepository.axiosInstance.delete<ChangeResponse>(`/Image/${id}`);
    }

    // endregion

    // region List

    static filterLists(request: FilterBaseRequest): Promise<AxiosResponse> {
        const params = {websiteId: request.websiteId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection};
        return HttpRepository.axiosInstance.get("/List", { params });
    }

    // endregion

    // region ListItem

    static getListItem(id: number): Promise<AxiosResponse> {
        const params = { id };
        return HttpRepository.axiosInstance.get("/ListItem", { params });
    }

    static createListItem(request: ListItemCreateRequest): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.post("/ListItem", request);
    }

    static updateListItem(request: ListItemResponse): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.put("/ListItem", request);
    }

    static deleteListItem(id: number): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.delete(`/ListItem/${id}`);
    }

    // endregion

    // region Section

    static getSection(id: number): Promise<AxiosResponse<Section>> {
        return HttpRepository.axiosInstance.get<Section>(`/Section/${id}`);
    }

    static filterSection(request: SectionFilterRequest): Promise<AxiosResponse<SectionFilterResponse>> {
        const params = {websiteId: request.websiteId, pageId: request.pageId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection};
        return HttpRepository.axiosInstance.get<SectionFilterResponse>("/Section/filter", { params });
    }

    static updateSection(request: UpdateSectionRequest): Promise<AxiosResponse<Section>> {
        return HttpRepository.axiosInstance.put("/Section", request);
    }

    // endregion

    // region Gallery

    static getGallery(id: number): Promise<AxiosResponse<Gallery>> {
        return HttpRepository.axiosInstance.get<Gallery>(`/Gallery/${id}`);
    }

    static filterGalleries(request: GalleryFilterRequest): Promise<AxiosResponse<GalleriesFilterResponse>> {
        const params = new URLSearchParams();
        params.set("websiteId", String(request.websiteId));
        params.set("limit", String(request.limit));
        params.set("offset", String(request.offset));
        params.set("orderDirection", request.orderDirection);
        (request.visibility ?? []).forEach(v => params.append("visibility", v));
        return HttpRepository.axiosInstance.get<GalleriesFilterResponse>("/Gallery", { params });
    }

    static createGallery(gallery: GallerySaveRequest): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.post("/Gallery", gallery);
    }

    static updateGallery(gallery: GallerySaveRequest): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.put("/Gallery", gallery);
    }

    static checkGalleryUsage(id: number): Promise<AxiosResponse<GalleryUsageResponse>> {
        const params = { id };
        return HttpRepository.axiosInstance.get<GalleryUsageResponse>("/Gallery/usage", { params });
    }

    static deleteGallery(id: number): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.delete(`/Gallery/${id}`);
    }

    // endregion

    // region Page

    static getPages(websiteId: number): Promise<AxiosResponse<GetPagesForWebsiteResponse>> {
        return HttpRepository.axiosInstance.get<GetPagesForWebsiteResponse>(`/Page/${websiteId}`);
    }

    static filterPages(request: FilterBaseRequest): Promise<AxiosResponse<PagesFilterResponse>> {
        const params = {websiteId: request.websiteId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection};
        return HttpRepository.axiosInstance.get<PagesFilterResponse>("/Page/filter", { params });
    }

    // endregion

    // region File

    static filterFiles(request: FilterBaseRequest): Promise<AxiosResponse<FilesResponse>> {
        const params = { websiteId: request.websiteId, limit: request.limit, offset: request.offset, orderDirection: request.orderDirection };
        return HttpRepository.axiosInstance.get<FilesResponse>('/File', { params });
    }

    static checkFileUsage(id: number): Promise<AxiosResponse<FileUsageResponse>> {
        return HttpRepository.axiosInstance.get<FileUsageResponse>(`/File/${id}`);
    }

    static uploadFiles(formData: FormData): Promise<AxiosResponse<FileItem[]>> {
        return HttpRepository.axiosInstance.post<FileItem[]>('/File', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    static editFileName(fileId: number, newFileName: string): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.put('/File', { fileId, newFileName });
    }

    static deleteFile(id: number): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.delete(`/File/${id}`);
    }

    // endregion

    // region Website

    static getWebsites(): Promise<AxiosResponse<GetWebsitesResponse>> {
        return HttpRepository.axiosInstance.get<GetWebsitesResponse>(`/Website`);
    }

    // endregion

    // region FrontendEntityChange

    static getFrontendEntityChnages(websiteId: number): Promise<AxiosResponse<FrontendEntityChangeListResponse>> {
        return HttpRepository.axiosInstance.get<FrontendEntityChangeListResponse>(`/FrontendEntityChange`, { params: { websiteId } });
    }

    static syncFrontendEntityChanges(websiteId: number): Promise<AxiosResponse> {
        return HttpRepository.axiosInstance.post(`/FrontendEntityChange`, { websiteId });
    }

    // endregion
}

export default HttpRepository;
